/**
 * TasksGuide: the visual guide of the tasks (TZ 2.2), opened by the "?" after the title of My tasks (/my-tasks,
 * HelpTitle helpId="tasks") and after the title of a subject's Tasks tab (FolderTasks, HelpButton id="tasks": both
 * share the seen mark).
 * It follows the reader's part the way the pages do:
 * - On a subject's Tasks tab, its moderator and admins (`canAssign`, FolderTasks' rule) get how to give tasks: a writing
 *   task (assignee, a matrix row, A, B and C prefilled with what the row's plan has not given out yet, due date,
 *   comment), the same from a matrix row's "Give a task" (the matrix's Tasks column: what is given out), a review task
 *   (the authors' sent questions but archived ones, later ones too, or chosen questions, up to MAX_TASK_QUESTIONS), the
 *   progress on each card, and the card's "⋯" (change, cancel) with "Show cancelled".
 * - Everyone else, and everyone on My tasks, gets their own tasks by what they may take (TaskDialog's TAKES): writing
 *   tasks (authors, experts, moderators): the card, "Write a question" and the create page's reminder, what counts;
 *   review tasks (experts, moderators): "Rate the rest: N" to the bank's not-yet-rated ones, the vote on the question
 *   page that counts. On My tasks the In progress / Done tabs, on the Tasks tab the subject's own list.
 * Phones: the matrix's row cards under 768px (FolderMatrix's breakpoint) and the framed "⋯".
 * Props: `inSubject` (the subject's Tasks tab, not My tasks), `canAssign` (the tab gives tasks: its moderator and
 * admins), `role` (the reader's role in the subject, on the tab).
 */
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarDaysIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  KeyIcon,
  PencilIcon,
  PencilSquareIcon,
  PlusIcon,
  QueueListIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon, HandThumbDownIcon, HandThumbUpIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { HelpFlow, HelpIntro, HelpLegend, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniCheck,
  MiniDialog,
  MiniDivider,
  MiniField,
  MiniIcon,
  MiniLevel,
  MiniMenu,
  MiniProgress,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniStepper,
  MiniTable,
  MiniTabs,
  MiniText,
} from "../Mini";
import { DIFFICULTY_LEVELS } from "../../Difficulty/Difficulty";
import { gradeLabel, levelsText } from "../../QuestionFolders/matrix/matrixModel";
import { MAX_TASK_QUESTIONS } from "../../QuestionFolders/tasks/taskModel";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canAssignIn, isAdminUser } from "../../../utils/folders";

// Who may take a task of a kind (TaskDialog's TAKES, the server's rule).
const TAKES = { write: ["author", "expert", "moderator"], review: ["expert", "moderator"] };
// The example matrix row: its plan and what tasks give out already; a new task gets the rest.
const PLAN = { A: 3, B: 2, C: 1 };
const GIVEN = { A: 1, B: 0, C: 0 };
const GRADE = 10;
// The example writing task's levels: planned, approved, sent and waiting (only the planned levels are shown).
const WRITE_LEVELS = [
  { level: "A", planned: 3, approved: 2, waiting: 0 },
  { level: "B", planned: 2, approved: 1, waiting: 1 },
];

export default function TasksGuide({ inSubject = false, canAssign = false, role = "" }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  // FolderMatrix's breakpoint: the matrix's table or its row cards; the "⋯" is framed on phones.
  const wide = useMediaQuery("(min-width: 768px)");
  const ex = (key) => t(`help.common.example.${key}`);
  const own = (key) => t(`help.tasks.example.${key}`);

  const isAdmin = isAdminUser(user);
  const folderRoles = user?.folder_roles || [];
  const takes = (kind) => (inSubject ? TAKES[kind].includes(role) : folderRoles.some((r) => TAKES[kind].includes(r)));
  // The Tasks tab of a subject the reader gives tasks in; elsewhere the reader's own tasks.
  const gives = inSubject && canAssign;
  const reviews = !gives && takes("review");
  // Whoever has My tasks writes or reviews somewhere; a writer's steps if the role says neither.
  const writes = !gives && (takes("write") || !reviews);
  // Tasks are given in a subject's tab: a tip on My tasks for those who give them somewhere.
  const givesSomewhere = isAdmin || folderRoles.some(canAssignIn);
  // My tasks opens for those who may take a task.
  const hasMine = folderRoles.some((r) => TAKES.write.includes(r));

  const kind = (key) => t(`folders.tasks.kind.${key}`);
  const tasksTab = t("folders.tab_tasks");
  const matrixTab = t("folders.tab_matrix");
  const mine = t("nav.my_tasks");
  const active = t("my_tasks.tab_active");
  const doneTab = t("my_tasks.tab_done");
  const doneState = t("folders.tasks.state.done");
  const subjectPath = `${ex("exam")} · ${ex("subject")}`;
  const writeTitle = `${ex("topic")} › ${own("subtopic")}`;
  const reviewTitle = t("folders.tasks.review_title", { count: 2 });
  const due = t("folders.tasks.due_on", { date: own("date") });
  const giveTask = t("folders.matrix.give_task");
  const byAuthors = t("folders.tasks.dialog.by_authors");
  const byQuestions = t("folders.tasks.dialog.by_questions");
  const remaining = Object.fromEntries(DIFFICULTY_LEVELS.map((level) => [level, Math.max(0, PLAN[level] - GIVEN[level])]));
  // A label with its count as the reader will see it, the number left open ("Rate the rest: N").
  const withN = (key) => t(key, { count: 2, replace: { count: "N" } });

  const kindBadge = (key) => <MiniBadge tone={key === "write" ? "indigo" : "violet"}>{kind(key)}</MiniBadge>;
  const doneBadge = (mark) => (
    <MiniBadge tone="green" icon={CheckCircleIcon} mark={mark}>
      {doneState}
    </MiniBadge>
  );
  // A writing task's levels as its card shows them: approved of planned, and how many more wait in review.
  const writeProgress = (done = false) =>
    WRITE_LEVELS.map(({ level, planned, approved, waiting }) => (
      <MiniStack key={level} tight>
        <MiniProgress value={done ? planned : approved} max={planned} label={<MiniLevel level={level} points={false} />} />
        {!done && waiting > 0 && (
          <MiniText variant="muted" truncate>
            {t("folders.tasks.waiting", { count: waiting })}
          </MiniText>
        )}
      </MiniStack>
    ));
  // A task's card, short: its kind (and Done), its title and, on My tasks, its folder and subject.
  const shortCard = (key, { done = false, doneMark, children } = {}) => (
    <MiniCard key={key}>
      <MiniRow>
        {kindBadge(key)}
        {done && doneBadge(doneMark)}
      </MiniRow>
      <MiniText variant="strong" truncate>
        {key === "write" ? writeTitle : reviewTitle}
      </MiniText>
      {!inSubject && (
        <MiniText variant="muted" truncate>
          {subjectPath}
        </MiniText>
      )}
      {children}
    </MiniCard>
  );
  // A writing task's whole card: its grade, assignee (for the moderator) and due date, its levels, its comment.
  const writeCard = ({ assignee = false, comment = false, mark } = {}) => (
    <MiniCard mark={mark}>
      <MiniRow>{kindBadge("write")}</MiniRow>
      <MiniText variant="strong" truncate>
        {writeTitle}
      </MiniText>
      <MiniRow>
        {!inSubject && <MiniText>{subjectPath}</MiniText>}
        <MiniText variant="muted">{gradeLabel(t, GRADE)}</MiniText>
        {assignee && <MiniText variant="muted">{t("folders.tasks.assignee_is", { name: own("author") })}</MiniText>}
        <MiniRow className="flex-nowrap">
          <MiniIcon icon={CalendarDaysIcon} />
          <MiniText variant="muted">{due}</MiniText>
        </MiniRow>
      </MiniRow>
      {writeProgress()}
      {comment && (
        <MiniCard tone="slate" icon={ChatBubbleLeftEllipsisIcon}>
          {own("comment")}
        </MiniCard>
      )}
    </MiniCard>
  );
  // The new task dialog's frame: its title and its Cancel / Give buttons.
  const taskDialog = (children, giveMark) => (
    <MiniDialog
      title={t("folders.tasks.dialog.new_title")}
      footer={[
        <MiniButton key="cancel" variant="outline">
          {t("buttons.cancel")}
        </MiniButton>,
        <MiniButton key="give" variant="primary" mark={giveMark}>
          {t("folders.tasks.dialog.give")}
        </MiniButton>,
      ]}
    >
      {children}
    </MiniDialog>
  );
  const assigned = t("folders.matrix.assigned", { levels: "A 1" });
  // The rules of the create page's reminder (WriteTaskReminder): what counts for the task and what does not.
  const rules = [
    ["topic", true, t("folders.tasks.reminder.rule_topic")],
    ["level", true, t("folders.tasks.reminder.rule_level")],
    ["send", true, t("folders.tasks.reminder.rule_send", { finish: t("my_questions.actions.finish_and_create") })],
    ["approved", true, t("folders.tasks.reminder.rule_approved")],
    ["not", false, t("folders.tasks.reminder.rule_not_counted")],
  ];

  return (
    <>
      <HelpIntro>{t("help.tasks.intro")}</HelpIntro>

      <HelpFlow pipeline="bts" current={gives ? "matrix" : reviews ? "review" : "questions"} />

      <HelpSteps>
        {gives && (
          <HelpStep
            title={t("help.tasks.steps.give_write.title")}
            illustration={taskDialog(
              <>
                <MiniText variant="muted" truncate>
                  {ex("subject")}
                </MiniText>
                <MiniTabs fill items={[kind("write"), kind("review")]} current={0} />
                <MiniRow>
                  <MiniField label={t("folders.tasks.dialog.assignee")} value={own("author")} select className="flex-1" />
                </MiniRow>
                <MiniRow>
                  <MiniField label={t("folders.tasks.dialog.row")} value={ex("topic")} select className="flex-1" mark="1" />
                </MiniRow>
                <MiniRow>
                  <MiniText variant="muted">{t("folders.tasks.dialog.row_plan", { plan: levelsText(PLAN), given: levelsText(GIVEN) })}</MiniText>
                </MiniRow>
                <MiniStack tight>
                  <MiniText variant="muted" truncate>
                    {t("folders.tasks.dialog.planned")}
                  </MiniText>
                  <MiniRow className="gap-y-3">
                    {DIFFICULTY_LEVELS.map((level) => (
                      <MiniStepper key={level} label={<MiniLevel level={level} />} value={remaining[level]} />
                    ))}
                  </MiniRow>
                </MiniStack>
              </>,
              "2"
            )}
          >
            <p>
              <HelpText
                i18nKey="help.tasks.steps.give_write.text"
                values={{ tab: tasksTab, button: t("folders.tasks.new_write"), give: t("folders.tasks.dialog.give") }}
              />
            </p>
            <p>{t("help.tasks.steps.give_write.text_who")}</p>
          </HelpStep>
        )}

        {gives && (
          <HelpStep
            title={t("help.tasks.steps.from_matrix.title")}
            illustration={
              <MiniScreen title={subjectPath}>
                {wide ? (
                  <MiniTable
                    columns={[
                      t("folders.matrix.col_topic_sub"),
                      ...DIFFICULTY_LEVELS.map((level) => ({ label: <MiniLevel level={level} points={false} />, width: "2.5rem", align: "center" })),
                      { label: t("folders.matrix.col_tasks"), width: "38%" },
                    ]}
                    rows={[
                      [
                        ex("topic"),
                        ...DIFFICULTY_LEVELS.map((level) => String(PLAN[level])),
                        <MiniStack tight>
                          <MiniText variant="muted" truncate>
                            {assigned}
                          </MiniText>
                          <MiniButton variant="link" icon={PlusIcon} mark>
                            {giveTask}
                          </MiniButton>
                        </MiniStack>,
                      ],
                      [
                        ex("topic_2"),
                        "2",
                        "1",
                        "1",
                        <MiniButton variant="link" icon={PlusIcon}>
                          {giveTask}
                        </MiniButton>,
                      ],
                    ]}
                  />
                ) : (
                  <MiniCard>
                    <MiniText variant="strong" truncate>
                      {ex("topic")}
                    </MiniText>
                    <MiniText truncate>{levelsText(PLAN)}</MiniText>
                    <MiniDivider />
                    <MiniText variant="muted" truncate>
                      {assigned}
                    </MiniText>
                    <MiniRow>
                      <MiniButton variant="link" icon={PlusIcon} mark>
                        {giveTask}
                      </MiniButton>
                    </MiniRow>
                  </MiniCard>
                )}
              </MiniScreen>
            }
          >
            <p>
              <HelpText
                i18nKey={wide ? "help.tasks.steps.from_matrix.text" : "help.tasks.steps.from_matrix.text_phone"}
                values={{ matrix: matrixTab, column: t("folders.matrix.col_tasks"), give: giveTask }}
              />
            </p>
            <p>
              <HelpText i18nKey="help.tasks.steps.from_matrix.text_over" values={{ over: t("folders.matrix.assigned_over") }} />
            </p>
          </HelpStep>
        )}

        {gives && (
          <HelpStep
            title={t("help.tasks.steps.give_review.title")}
            illustration={taskDialog(
              <>
                <MiniTabs fill items={[kind("write"), kind("review")]} current={1} mark={1} />
                <MiniRow>
                  <MiniField label={t("folders.tasks.dialog.assignee")} value={own("expert")} select className="flex-1" />
                </MiniRow>
                <MiniStack tight>
                  <MiniText variant="muted" truncate>
                    {t("folders.tasks.dialog.review_by")}
                  </MiniText>
                  <MiniTabs fill items={[byAuthors, byQuestions]} current={0} />
                </MiniStack>
                <MiniCard>
                  {[own("author"), own("author_2")].map((name) => (
                    <MiniRow key={name} className="justify-between">
                      <MiniCheck checked>{name}</MiniCheck>
                      <MiniText variant="muted">{t("folders.roles.author")}</MiniText>
                    </MiniRow>
                  ))}
                </MiniCard>
              </>
            )}
          >
            <p>
              <HelpText
                i18nKey="help.tasks.steps.give_review.text"
                values={{ button: t("folders.tasks.new_review"), authors: byAuthors, questions: byQuestions, max: MAX_TASK_QUESTIONS }}
              />
            </p>
            <p>{t("help.tasks.steps.give_review.text_skip")}</p>
          </HelpStep>
        )}

        {gives && (
          <HelpStep
            title={t("help.tasks.steps.progress.title")}
            illustration={
              <MiniStack>
                <MiniRow>
                  <MiniText variant="muted">{t("folders.tasks.count_active", { count: 2 })}</MiniText>
                  <MiniText variant="muted">{t("folders.tasks.count_done", { count: 1 })}</MiniText>
                </MiniRow>
                {writeCard({ assignee: true, mark: { pointer: false } })}
                {shortCard("review", {
                  children: (
                    <>
                      <MiniProgress value={3} max={8} />
                      <MiniRow className="gap-y-3">
                        <MiniButton variant="link">{t("folders.tasks.open_questions")}</MiniButton>
                        <MiniButton variant="quiet">{t("folders.tasks.open_unvoted", { count: 5 })}</MiniButton>
                      </MiniRow>
                    </>
                  ),
                })}
              </MiniStack>
            }
          >
            <p>{t("help.tasks.steps.progress.text")}</p>
            <p>
              <HelpText i18nKey="help.tasks.steps.progress.text_open" values={{ open: t("folders.tasks.open_questions") }} />
            </p>
          </HelpStep>
        )}

        {gives && (
          <HelpStep
            title={t("help.tasks.steps.change.title")}
            illustration={
              <MiniStack>
                <MiniCard aside={<MiniButton variant={wide ? "quiet" : "outline"} icon={EllipsisHorizontalIcon} mark="1" />}>
                  <MiniRow>{kindBadge("write")}</MiniRow>
                  <MiniText variant="strong" truncate>
                    {writeTitle}
                  </MiniText>
                </MiniCard>
                <MiniRow className="justify-end">
                  <MiniMenu
                    items={[
                      { label: t("folders.tasks.edit"), icon: PencilSquareIcon, mark: "2" },
                      { label: t("folders.tasks.cancel"), icon: XCircleIcon, danger: true },
                    ]}
                  />
                </MiniRow>
                <MiniRow>
                  <MiniCheck>{t("folders.tasks.show_cancelled")}</MiniCheck>
                </MiniRow>
              </MiniStack>
            }
          >
            <p>
              <HelpText i18nKey="help.tasks.steps.change.text" values={{ edit: t("folders.tasks.edit"), cancel: t("folders.tasks.cancel") }} />
            </p>
            <p>
              <HelpText i18nKey="help.tasks.steps.change.text_show" values={{ show: t("folders.tasks.show_cancelled") }} />
            </p>
          </HelpStep>
        )}

        {!gives && (
          <HelpStep
            title={t("help.tasks.steps.find.title")}
            illustration={
              inSubject ? (
                <MiniScreen title={subjectPath}>
                  <MiniTabs
                    fill
                    items={[
                      { label: t("folders.tab_questions"), icon: QueueListIcon },
                      { label: matrixTab, icon: TableCellsIcon },
                      { label: tasksTab, icon: ClipboardDocumentCheckIcon },
                    ]}
                    current={2}
                    mark={2}
                  />
                  <MiniText variant="muted" truncate>
                    {t("folders.tasks.subtitle_mine")}
                  </MiniText>
                  {writes && shortCard("write")}
                  {reviews && shortCard("review")}
                </MiniScreen>
              ) : (
                <MiniScreen title={mine}>
                  <MiniTabs fill items={[active, doneTab]} current={0} mark={0} />
                  {writes && shortCard("write")}
                  {reviews && shortCard("review")}
                </MiniScreen>
              )
            }
          >
            {inSubject ? (
              <HelpText i18nKey="help.tasks.steps.find.text_subject" values={{ tab: tasksTab, mine }} />
            ) : (
              <HelpText i18nKey="help.tasks.steps.find.text" values={{ active, done: doneTab }} />
            )}
          </HelpStep>
        )}

        {writes && (
          <HelpStep title={t("help.tasks.steps.read.title")} illustration={writeCard({ comment: true })}>
            <p>{t("help.tasks.steps.read.text")}</p>
            <p>
              <HelpText i18nKey="help.tasks.steps.read.text_waiting" values={{ waiting: withN("folders.tasks.waiting") }} />
            </p>
          </HelpStep>
        )}

        {writes && (
          <HelpStep
            title={t("help.tasks.steps.write.title")}
            illustration={
              <MiniStack>
                {shortCard("write", {
                  children: (
                    <MiniRow>
                      <MiniButton variant="link" mark="1">
                        {t("folders.tasks.write_question")}
                      </MiniButton>
                    </MiniRow>
                  ),
                })}
                <MiniCard
                  tone="blue"
                  icon={ClipboardDocumentCheckIcon}
                  aside={<MiniText variant="muted">{t("folders.tasks.reminder.hide")}</MiniText>}
                  mark={{ label: "2" }}
                >
                  <MiniText variant="strong" truncate>
                    {t("folders.tasks.reminder.title", { count: 1 })}
                  </MiniText>
                  <MiniRow>
                    <MiniLevel level="A" points={false} />
                    <MiniText>{t("folders.tasks.reminder.level_count", { planned: 3, approved: 2 })}</MiniText>
                  </MiniRow>
                </MiniCard>
              </MiniStack>
            }
          >
            <HelpText
              i18nKey="help.tasks.steps.write.text"
              values={{ write: t("folders.tasks.write_question"), reminder: t("folders.tasks.reminder.title", { count: 1 }) }}
            />
          </HelpStep>
        )}

        {writes && (
          <HelpStep
            title={t("help.tasks.steps.counts.title")}
            illustration={
              <MiniCard>
                <MiniText variant="strong" truncate>
                  {t("folders.tasks.reminder.rules_title")}
                </MiniText>
                {rules.map(([key, ok, text]) => (
                  <MiniRow key={key} className="flex-nowrap">
                    <MiniIcon icon={ok ? CheckIcon : XMarkIcon} tone={ok ? "green" : "red"} />
                    <MiniText>{text}</MiniText>
                  </MiniRow>
                ))}
              </MiniCard>
            }
          >
            <p>{t("help.tasks.steps.counts.text")}</p>
            <p>
              <HelpText i18nKey="help.tasks.steps.counts.text_send" values={{ finish: t("my_questions.actions.finish_and_create") }} />
            </p>
          </HelpStep>
        )}

        {reviews && (
          <HelpStep
            title={t("help.tasks.steps.rate.title")}
            illustration={
              <MiniStack>
                {shortCard("review", {
                  children: (
                    <>
                      <MiniProgress value={3} max={8} />
                      <MiniRow className="gap-y-3">
                        <MiniButton variant="link" mark>
                          {t("folders.tasks.open_left", { count: 5 })}
                        </MiniButton>
                        <MiniButton variant="quiet">{t("folders.tasks.open_all")}</MiniButton>
                      </MiniRow>
                    </>
                  ),
                })}
                <MiniRow>
                  <MiniBadge tone="blue">{`${t("folders.tasks.task_chip")}: ${reviewTitle}`}</MiniBadge>
                  <MiniBadge tone="blue">{`${t("folders.tasks.left_chip")}: ${t("folders.tasks.left_chip_value")}`}</MiniBadge>
                </MiniRow>
              </MiniStack>
            }
          >
            <HelpText
              i18nKey="help.tasks.steps.rate.text"
              values={{ left: withN("folders.tasks.open_left"), all: t("folders.tasks.open_all"), chip: `${t("folders.tasks.task_chip")}: …` }}
            />
          </HelpStep>
        )}

        {reviews && (
          <HelpStep
            title={t("help.tasks.steps.vote.title")}
            illustration={
              <MiniCard>
                <MiniCard tone="blue" icon={ClipboardDocumentCheckIcon}>
                  {t("question_detail.vote_bar.task_todo", { task: reviewTitle })}
                </MiniCard>
                <MiniRow className="justify-between gap-y-3">
                  <MiniText variant="strong">{t("question_detail.vote_bar.title")}</MiniText>
                  <MiniRow>
                    <MiniButton variant="outline" icon={HandThumbUpIcon} mark>
                      {t("question_detail.vote_bar.like")}
                    </MiniButton>
                    <MiniButton variant="outline" icon={HandThumbDownIcon}>
                      {t("question_detail.vote_bar.dislike")}
                    </MiniButton>
                  </MiniRow>
                </MiniRow>
              </MiniCard>
            }
          >
            <p>
              <HelpText
                i18nKey="help.tasks.steps.vote.text"
                values={{ up: t("question_detail.vote_bar.like"), down: t("question_detail.vote_bar.dislike") }}
              />
            </p>
            <p>{t("help.tasks.steps.vote.text_decision")}</p>
          </HelpStep>
        )}

        {!gives && (
          <HelpStep
            title={t("help.tasks.steps.done.title")}
            illustration={
              inSubject ? (
                shortCard(writes ? "write" : "review", { done: true, doneMark: { pointer: false }, children: writes ? writeProgress(true) : <MiniProgress value={8} max={8} /> })
              ) : (
                <MiniScreen title={mine}>
                  <MiniTabs fill items={[active, doneTab]} current={1} mark={1} />
                  {shortCard(writes ? "write" : "review", { done: true, children: writes ? writeProgress(true) : <MiniProgress value={8} max={8} /> })}
                </MiniScreen>
              )
            }
          >
            {inSubject ? (
              <HelpText i18nKey="help.tasks.steps.done.text_subject" values={{ state: doneState }} />
            ) : (
              <HelpText i18nKey="help.tasks.steps.done.text" values={{ done: doneTab }} />
            )}
          </HelpStep>
        )}
      </HelpSteps>

      <HelpLegend
        rows={[
          { key: "done", label: doneState, tone: "green", icon: CheckCircleIcon, text: t("help.tasks.legend.done") },
          { key: "overdue", label: t("folders.tasks.state.overdue"), tone: "red", icon: ExclamationTriangleIcon, text: t("help.tasks.legend.overdue") },
          // Only those who give tasks see the cancelled ones.
          gives && { key: "cancelled", label: t("folders.tasks.state.cancelled"), tone: "slate", icon: XCircleIcon, text: t("help.tasks.legend.cancelled") },
        ]}
      />

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.tasks.roles.admin") },
          { key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.tasks.roles.moderator") },
          { key: "expert", role: t("folders.roles.expert"), Icon: CheckBadgeIcon, can: t("help.tasks.roles.expert") },
          { key: "author", role: t("folders.roles.author"), Icon: PencilIcon, can: t("help.tasks.roles.author") },
          { key: "viewer", role: t("folders.roles.viewer"), Icon: EyeIcon, can: t("help.tasks.roles.viewer") },
        ]}
      />

      <HelpTips
        items={[
          gives && <HelpText key="share" i18nKey="help.tasks.tips.share" values={{ share: t("folders.share.button") }} />,
          gives && t("help.tasks.tips.duplicate"),
          gives && hasMine && <HelpText key="mine" i18nKey="help.tasks.tips.mine" values={{ mine }} />,
          reviews && t("help.tasks.tips.earlier"),
          writes && t("help.tasks.tips.reminder_all"),
          writes && t("help.tasks.tips.not_in_matrix"),
          !inSubject && givesSomewhere && <HelpText key="give" i18nKey="help.tasks.tips.give" values={{ tab: tasksTab }} />,
        ]}
      />
    </>
  );
}
